/* eslint-disable sort-imports */
import {
    BulletinAdminTabs,
    UPDATE_BULLETIN_PATH,
} from 'src/components/bulletins-form/tabs';
import { BulletinsService } from 'src/services/bulletins.service';
import BulletinsAdminTable from 'src/components/bulletins-admin-table';
import {
    BulletinActionDialog,
    IBulletinActionResult,
    useBulletinActions,
} from 'src/components/bulletins-admin-table/bulletin-actions';
import { ComboBox } from 'src/components/ui/combobox';
import { DatePicker } from 'src/components/date-picker';
import FooterPrivacy from 'src/views/auth/components/baseTemplate/FooterPrivacy';
import { HttpStatusCode } from 'src/utils/enums/http-method.enum';
import { IAdminBulletinItem } from 'src/services/models/bulletins.service';
import Notification, {
    NotificationType,
} from 'src/components/ui/notification/Notification';
import Spinner from 'src/components/ui/spinner';
import TableFooter from 'src/components/table/pagination/tableFooter';
import { useLocalization } from 'src/utils/useLocalization';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    BULLETIN_TYPE_CODES,
    BulletinSortColumn,
    BulletinSortDirection,
    formatDate,
    formatDateForApi,
    getDefaultDateRange,
    getErrorStatus,
    sortBulletins,
} from 'src/utils/bulletins';

import '../upload-bulletin/styles.scss';
import './view-bulletin.scss';

const NOT_FOUND_STATUS = 404;
const SCROLL_TOP = 0;
const EMPTY_RESULTS = 0;
const INITIAL_REQUEST_ID = 0;
const INITIAL_FILTER_RESET_KEY = 0;
const RESET_KEY_INCREMENT = 1;
const DEFAULT_PAGE_SIZE = 10;
const PAGINATION_BRAND = 'vw6';

type StatusFilter = 'All' | 'Active' | 'Expired';
type SearchField = 'title' | 'bulletinNumber';

interface IBulletinFilters {
    searchInput: string;
    statusFilter: StatusFilter;
    typeFilter: string;
    startDate: string;
    endDate: string;
}

const getDefaultFilters = (range: {
    startDate: string;
    endDate: string;
}): IBulletinFilters => ({
    searchInput: '',
    statusFilter: 'Active',
    typeFilter: '',
    startDate: range.startDate,
    endDate: range.endDate,
});

const ViewBulletin: React.FC = () => {
    const { t } = useTranslation();
    const { bulletins, labelText } = useLocalization();
    const navigate = useNavigate();
    const location = useLocation();
    const text = (key: string, defaultValue: string) =>
        t(`SystemAdmin.ViewBulletinPage.${key}`, { defaultValue });

    const bulletinTypeOptions = [
        {
            code: 'Informational',
            label: bulletins.LABEL_BULLETINS_TYPE_INFORMATIONAL,
        },
        { code: 'Programs', label: bulletins.LABEL_BULLETINS_TYPE_PROGRAMS },
    ];

    const [defaultDateRange] = useState(getDefaultDateRange);
    const [isLoading, setIsLoading] = useState(true);
    const [dataset, setDataset] = useState<IAdminBulletinItem[]>([]);
    const [displayedContent, setDisplayedContent] = useState<
        IAdminBulletinItem[]
    >([]);
    const [searchInput, setSearchInput] = useState('');
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('Active');
    const [typeFilter, setTypeFilter] = useState('');
    const [startDate, setStartDate] = useState(defaultDateRange.startDate);
    const [endDate, setEndDate] = useState(defaultDateRange.endDate);
    const [filterResetKey, setFilterResetKey] = useState(
        INITIAL_FILTER_RESET_KEY,
    );
    const [sortColumn, setSortColumn] = useState<BulletinSortColumn>('endDate');
    const [sortDirection, setSortDirection] =
        useState<BulletinSortDirection>('asc');
    const [bannerMessage, setBannerMessage] = useState('');
    const [isBannerError, setIsBannerError] = useState(false);
    const [isBannerVisible, setIsBannerVisible] = useState(false);

    const mountedRef = useRef(false);
    const latestRequestRef = useRef(INITIAL_REQUEST_ID);

    const showBanner = (message: string, isError: boolean) => {
        setBannerMessage(message);
        setIsBannerError(isError);
        setIsBannerVisible(true);
        window.scrollTo(SCROLL_TOP, SCROLL_TOP);
    };

    const handleActionSuccess = ({
        type,
        item,
        message,
    }: IBulletinActionResult) => {
        setDataset((currentItems) =>
            type === 'delete'
                ? currentItems.filter(
                      (current) => current.bulletinId !== item.bulletinId,
                  )
                : currentItems.map((current) =>
                      current.bulletinId === item.bulletinId
                          ? {
                                ...current,
                                validThrough: formatDateForApi(
                                    formatDate(new Date()),
                                ),
                            }
                          : current,
                  ),
        );
        showBanner(message, false);
    };

    const { requestExpire, requestDelete, dialogProps } = useBulletinActions({
        onSuccess: handleActionSuccess,
        onError: (message) => showBanner(message, true),
    });

    useEffect(() => {
        const message = (location.state as { successMessage?: string } | null)
            ?.successMessage;

        if (message) {
            showBanner(message, false);
            navigate(location.pathname, { replace: true, state: null });
        }
    }, []);

    useEffect(() => {
        mountedRef.current = true;

        return () => {
            mountedRef.current = false;
            latestRequestRef.current += RESET_KEY_INCREMENT;
        };
    }, []);

    const fetchBulletins = useCallback(async (filters: IBulletinFilters) => {
        const requestId = ++latestRequestRef.current;
        const isLatest = () =>
            mountedRef.current && requestId === latestRequestRef.current;
        const service = new BulletinsService();
        const trimmedSearch = filters.searchInput.trim();
        const searchTypes: Array<'ACTIVE' | 'EXPIRED'> =
            filters.statusFilter === 'Active'
                ? ['ACTIVE']
                : filters.statusFilter === 'Expired'
                ? ['EXPIRED']
                : ['ACTIVE', 'EXPIRED'];
        const searchFields: SearchField[] = trimmedSearch
            ? ['title', 'bulletinNumber']
            : ['title'];
        const bulletinType = filters.typeFilter
            ? BULLETIN_TYPE_CODES[filters.typeFilter]
            : null;

        setIsLoading(true);

        try {
            const responses = await Promise.allSettled(
                searchTypes.flatMap((searchType) =>
                    searchFields.map((searchField) =>
                        service.getBulletinListAdmin({
                            searchType,
                            startDate: formatDateForApi(filters.startDate),
                            endDate: formatDateForApi(filters.endDate),
                            title:
                                searchField === 'title'
                                    ? trimmedSearch || null
                                    : null,
                            bulletinNumber:
                                searchField === 'bulletinNumber'
                                    ? trimmedSearch
                                    : null,
                            bulletinType,
                        }),
                    ),
                ),
            );
            const results: IAdminBulletinItem[] = [];
            let realError: unknown;

            responses.forEach((response) => {
                if (response.status === 'fulfilled') {
                    const { status, data } = response.value;

                    if (status === HttpStatusCode.SUCCESS) {
                        if (Array.isArray(data)) {
                            results.push(...(data as IAdminBulletinItem[]));
                        } else {
                            realError ??= new Error(
                                'Invalid bulletin response',
                            );
                        }
                    } else if (status !== NOT_FOUND_STATUS) {
                        realError ??= new Error(
                            `Unable to retrieve bulletins. Status: ${status}`,
                        );
                    }

                    return;
                }

                if (getErrorStatus(response.reason) !== NOT_FOUND_STATUS) {
                    realError ??= response.reason;
                }
            });

            if (realError !== undefined) {
                throw realError;
            }

            const uniqueResults = Array.from(
                new Map(
                    results.map((item) => [item.bulletinId, item]),
                ).values(),
            );

            if (isLatest()) {
                setSortColumn('endDate');
                setSortDirection('asc');
                setDisplayedContent([]);
                setDataset(sortBulletins(uniqueResults, 'endDate', 'asc'));
            }
        } catch (error) {
            if (isLatest()) {
                console.error('Unable to retrieve bulletins', error);
                setDisplayedContent([]);
                setDataset([]);
            }
        } finally {
            if (isLatest()) {
                setIsLoading(false);
            }
        }
    }, []);

    useEffect(() => {
        void fetchBulletins(getDefaultFilters(defaultDateRange));
    }, [fetchBulletins, defaultDateRange.startDate, defaultDateRange.endDate]);

    const statusAllLabel = text('StatusAll', 'All');
    const statusActiveLabel = text('StatusActive', 'Active');
    const statusExpiredLabel = text('StatusExpired', 'Expired');

    /*
     * Every control re-runs the search with ALL the filters currently
     * applied; only the changed ones are overridden. Read from a ref so
     * the callbacks stay stable (DatePicker re-initializes when its
     * handler changes).
     */
    const latestFiltersRef = useRef<IBulletinFilters>(
        getDefaultFilters(defaultDateRange),
    );

    latestFiltersRef.current = {
        searchInput,
        statusFilter,
        typeFilter,
        startDate,
        endDate,
    };

    const applyFilters = useCallback(
        (changes: Partial<IBulletinFilters> = {}) => {
            void fetchBulletins({ ...latestFiltersRef.current, ...changes });
        },
        [fetchBulletins],
    );

    const handleStatusSelect = (value: string) => {
        const nextStatus: StatusFilter =
            value === statusActiveLabel
                ? 'Active'
                : value === statusExpiredLabel
                ? 'Expired'
                : 'All';

        if (nextStatus === statusFilter) {
            return;
        }

        setStatusFilter(nextStatus);
        applyFilters({ statusFilter: nextStatus });
    };

    const handleTypeSelect = (value: string) => {
        const nextType =
            bulletinTypeOptions.find((option) => option.label === value)
                ?.code ?? '';

        if (nextType === typeFilter) {
            return;
        }

        setTypeFilter(nextType);
        applyFilters({ typeFilter: nextType });
    };

    const handleDateRangeClose = useCallback(
        (nextStartDate: string, nextEndDate: string) => {
            const resolvedStartDate =
                nextStartDate || defaultDateRange.startDate;
            const resolvedEndDate = nextEndDate || defaultDateRange.endDate;

            if (
                resolvedStartDate === latestFiltersRef.current.startDate &&
                resolvedEndDate === latestFiltersRef.current.endDate
            ) {
                return;
            }

            setStartDate(resolvedStartDate);
            setEndDate(resolvedEndDate);
            applyFilters({
                startDate: resolvedStartDate,
                endDate: resolvedEndDate,
            });
        },
        [defaultDateRange.startDate, defaultDateRange.endDate, applyFilters],
    );

    const handleSearchKeyDown = (
        event: React.KeyboardEvent<HTMLInputElement>,
    ) => {
        if (event.key === 'Enter') {
            applyFilters();
        }
    };

    const clearSearchText = () => {
        setSearchInput('');
        applyFilters({ searchInput: '' });
    };

    const clearFilters = () => {
        setSearchInput('');
        setStatusFilter('Active');
        setTypeFilter('');
        setStartDate(defaultDateRange.startDate);
        setEndDate(defaultDateRange.endDate);
        setFilterResetKey((currentKey) => currentKey + RESET_KEY_INCREMENT);
        void fetchBulletins(getDefaultFilters(defaultDateRange));
    };

    const handleSort = (column: BulletinSortColumn) => {
        const nextDirection: BulletinSortDirection =
            column === sortColumn && sortDirection === 'asc' ? 'desc' : 'asc';

        setSortColumn(column);
        setSortDirection(nextDirection);
        setDataset((currentItems) =>
            sortBulletins(currentItems, column, nextDirection),
        );
    };

    const handleSelectBulletin = (bulletin: IAdminBulletinItem) => {
        navigate(UPDATE_BULLETIN_PATH, { state: { bulletin } });
    };

    return (
        <>
            <main className='upload-bulletin view-bulletin'>
                {bannerMessage && (
                    <Notification
                        content={bannerMessage}
                        type={
                            isBannerError
                                ? NotificationType.ERROR
                                : NotificationType.SUCCESS
                        }
                        visibility={isBannerVisible}
                        setVisibility={setIsBannerVisible}
                    />
                )}
                <div className='page-title'>
                    <h1>{text('PageTitle', 'Online Bulletins')}</h1>
                </div>

                <BulletinAdminTabs active='update' />

                <section className='view-bulletin__panel'>
                    <h2>{bulletins.LABEL_BULLETINS_SEARCH}</h2>
                    <p className='view-bulletin__description'>
                        {bulletins.LABEL_BULLETINS_SEARCH_DESCRIPTION}
                    </p>

                    <div className='view-bulletin__search-row'>
                        <div className='c-input search-text view-bulletin__search-input'>
                            <span id='searchIcon'>
                                <i
                                    className='c-icon c-icon--[semantic-search]'
                                    aria-hidden='true'
                                    role='img'
                                />
                            </span>
                            <input
                                aria-label={
                                    bulletins.LABEL_BULLETINS_SEARCH_PLACEHOLDER
                                }
                                className='c-input__input'
                                placeholder={
                                    bulletins.LABEL_BULLETINS_SEARCH_PLACEHOLDER
                                }
                                value={searchInput}
                                onChange={(event) =>
                                    setSearchInput(event.target.value)
                                }
                                onKeyDown={handleSearchKeyDown}
                            />
                            {searchInput && (
                                <button
                                    className='view-bulletin__search-clear'
                                    type='button'
                                    aria-label={
                                        bulletins.LABEL_BULLETINS_CLEAR_SEARCH
                                    }
                                    onClick={clearSearchText}
                                >
                                    <i
                                        className='c-icon c-icon--[semantic-close]'
                                        aria-hidden='true'
                                    />
                                </button>
                            )}
                        </div>

                        <label>
                            <span>{bulletins.LABEL_BULLETINS_STATUS}</span>
                            <ComboBox
                                key={`status-${filterResetKey}`}
                                name='bulletin-status'
                                options={[
                                    statusAllLabel,
                                    statusActiveLabel,
                                    statusExpiredLabel,
                                ]}
                                selectedOption={
                                    statusFilter === 'Active'
                                        ? statusActiveLabel
                                        : statusFilter === 'Expired'
                                        ? statusExpiredLabel
                                        : statusAllLabel
                                }
                                onSelect={handleStatusSelect}
                            />
                        </label>

                        <label>
                            <span>{bulletins.LABEL_BULLETINS_TYPE}</span>
                            <ComboBox
                                key={`type-${filterResetKey}`}
                                name='bulletin-type'
                                options={[
                                    bulletins.LABEL_BULLETINS_TYPE_ALL,
                                    ...bulletinTypeOptions.map(
                                        (option) => option.label,
                                    ),
                                ]}
                                selectedOption={
                                    bulletinTypeOptions.find(
                                        (option) => option.code === typeFilter,
                                    )?.label ??
                                    bulletins.LABEL_BULLETINS_TYPE_ALL
                                }
                                onSelect={handleTypeSelect}
                            />
                        </label>

                        <label className='date-picker-container'>
                            <span>{bulletins.LABEL_BULLETINS_DATE_RANGE}</span>
                            <DatePicker
                                key={`date-range-${filterResetKey}`}
                                startDate={startDate}
                                endDate={endDate}
                                handleOnClose={handleDateRangeClose}
                                unrestrictedDates
                            />
                        </label>

                        <button
                            className='c-btn c-btn--link'
                            type='button'
                            onClick={clearFilters}
                        >
                            {bulletins.LABEL_BULLETINS_CLEAR_FILTERS}
                        </button>
                    </div>

                    <div className='view-bulletin__results'>
                        {isLoading ? (
                            <div className='loader_container'>
                                <Spinner />
                            </div>
                        ) : dataset.length === EMPTY_RESULTS ? (
                            <div className='view-bulletin__empty'>
                                {labelText.LABEL_TEXT_DASH_NORESULTS}
                            </div>
                        ) : (
                            <>
                                <BulletinsAdminTable
                                    items={displayedContent}
                                    sortColumn={sortColumn}
                                    sortDirection={sortDirection}
                                    onSort={handleSort}
                                    onSelectBulletin={handleSelectBulletin}
                                    onExpire={requestExpire}
                                    onDelete={requestDelete}
                                />
                                <TableFooter
                                    brand={PAGINATION_BRAND}
                                    content={dataset}
                                    setDisplayedContent={setDisplayedContent}
                                    defaultPerPage={DEFAULT_PAGE_SIZE}
                                />
                            </>
                        )}
                    </div>
                </section>
            </main>
            <BulletinActionDialog {...dialogProps} />
            <FooterPrivacy />
        </>
    );
};

export default ViewBulletin;
